import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiEye, FiShoppingCart, FiMousePointer, FiUsers, FiTrendingUp, FiDollarSign, FiCalendar, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [customYear, setCustomYear] = useState(new Date().getFullYear());
  const [analytics, setAnalytics] = useState({
    pageViews: 0,
    uniqueVisitors: 0,
    topProductsViewed: [],
    topProductsAdded: [],
    topProductsSold: [],
    topPages: [],
    conversionRate: 0,
    cartAbandonmentRate: 0
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, customYear]);

  const getDateRange = () => {
    const now = new Date();
    const startDate = new Date();

    switch (dateRange) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        break;
      case '3d':
        startDate.setDate(now.getDate() - 3);
        break;
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(now.getDate() - 90);
        break;
      case '180d':
        startDate.setDate(now.getDate() - 180);
        break;
      case '365d':
        startDate.setDate(now.getDate() - 365);
        break;
      case 'year':
        startDate.setFullYear(customYear, 0, 1);
        startDate.setHours(0, 0, 0, 0);
        return { start: startDate, end: new Date(customYear, 11, 31, 23, 59, 59) };
      default:
        startDate.setHours(0, 0, 0, 0);
    }

    return { start: startDate, end: now };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    
    const { data: events, error } = await supabase
      .from('analytics_events')
      .select('event_type, product_id, session_id, page_url, created_at, products(name, image)')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    const { data: orders } = await supabase
      .from('orders')
      .select('status, created_at, order_items(quantity, product_id, products(name, image))')
      .neq('status', 'draft')
      .neq('status', 'cancelled')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());

    if (error) {
      console.error("Error fetching analytics:", error);
      setLoading(false);
      return;
    }

    if (!events || events.length === 0) {
      setAnalytics({
        pageViews: 0,
        uniqueVisitors: 0,
        topProductsViewed: [],
        topProductsAdded: [],
        topProductsSold: [],
        topPages: [],
        conversionRate: 0,
        cartAbandonmentRate: 0
      });
      setLoading(false);
      return;
    }

    const pageViews = events.filter(e => e.event_type === 'page_view').length;
    const uniqueVisitors = new Set(events.map(e => e.session_id)).size;
    
    const productViews = {};
    const productAdds = {};
    const pageCounts = {};
    
    events.forEach(e => {
      if (e.product_id && e.products) {
        const key = e.product_id;
        if (e.event_type === 'product_view') {
          if (!productViews[key]) {
            productViews[key] = { id: key, name: e.products.name, image: e.products.image, count: 0 };
          }
          productViews[key].count += 1;
        } else if (e.event_type === 'add_to_cart') {
          if (!productAdds[key]) {
            productAdds[key] = { id: key, name: e.products.name, image: e.products.image, count: 0 };
          }
          productAdds[key].count += 1;
        }
      }
      
      if (e.event_type === 'page_view' && e.page_url) {
        pageCounts[e.page_url] = (pageCounts[e.page_url] || 0) + 1;
      }
    });
    
    const topViewed = Object.values(productViews)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
      
    const topAdded = Object.values(productAdds)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
      
    const topPages = Object.entries(pageCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(([url, count]) => ({ url, count }));

    const productSales = {};
    (orders || []).forEach(order => {
      if (order.order_items) {
        order.order_items.forEach(item => {
          if (item.products && item.product_id) {
            const key = item.product_id;
            if (!productSales[key]) {
              productSales[key] = { id: key, name: item.products.name, image: item.products.image, count: 0 };
            }
            productSales[key].count += item.quantity;
          }
        });
      }
    });
    const topSold = Object.values(productSales)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    const totalOrders = (orders || []).length;
    const conversionRate = uniqueVisitors > 0 ? (totalOrders / uniqueVisitors) * 100 : 0;

    const totalCartAdds = topAdded.reduce((a, b) => a + b.count, 0);
    const cartAbandonmentRate = totalCartAdds > 0 
      ? ((totalCartAdds - totalOrders) / totalCartAdds) * 100 
      : 0;

    setAnalytics({
      pageViews,
      uniqueVisitors,
      topProductsViewed: topViewed,
      topProductsAdded: topAdded,
      topProductsSold: topSold,
      topPages,
      conversionRate,
      cartAbandonmentRate
    });
    setLoading(false);
  };

  const getDateRangeLabel = () => {
    const labels = {
      'today': 'Today',
      '3d': 'Last 3 Days',
      '7d': 'Last 7 Days',
      '30d': 'Last 30 Days',
      '90d': 'Last 3 Months',
      '180d': 'Last 6 Months',
      '365d': 'Last Year',
      'year': `Year ${customYear}`
    };
    return labels[dateRange] || 'Today';
  };

  const ProductLink = ({ product }) => (
    <a
      href={`/product/${product.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 flex-1 min-w-0 group/link hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
    >
      {product.image && (
        <img 
          src={product.image} 
          alt={product.name}
          className="w-8 h-8 rounded-md object-cover border border-gray-200 flex-shrink-0"
        />
      )}
      <span className="text-sm text-gray-800 truncate group-hover/link:text-brand-gold transition-colors">
        {product.name}
      </span>
      <SafeIcon 
        icon={FiExternalLink} 
        className="text-gray-300 group-hover/link:text-brand-gold flex-shrink-0 transition-colors" 
        size={12} 
      />
    </a>
  );

  const PageLink = ({ url }) => {
    const isProductPage = url.startsWith('/product/');
    const displayUrl = url.length > 50 ? url.substring(0, 50) + '...' : url;
    
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-gray-800 font-mono hover:text-brand-gold transition-colors inline-flex items-center gap-1.5 group/link"
      >
        <span className="truncate max-w-xs">{displayUrl}</span>
        <SafeIcon 
          icon={FiExternalLink} 
          className="text-gray-300 group-hover/link:text-brand-gold flex-shrink-0 transition-colors" 
          size={12} 
        />
        {isProductPage && (
          <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-sans font-bold uppercase flex-shrink-0">
            Product
          </span>
        )}
      </a>
    );
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-500 text-sm">Loading analytics data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <FiCalendar className="text-brand-gold w-5 h-5" />
          <h2 className="text-sm font-bold uppercase tracking-widest text-gray-800">Date Range</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'today', label: 'Today' },
            { value: '3d', label: '3 Days' },
            { value: '7d', label: '1 Week' },
            { value: '30d', label: '1 Month' },
            { value: '90d', label: '3 Months' },
            { value: '180d', label: '6 Months' },
            { value: '365d', label: '1 Year' },
            { value: 'year', label: 'Custom Year' }
          ].map(range => (
            <button
              key={range.value}
              onClick={() => setDateRange(range.value)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                dateRange === range.value
                  ? 'bg-brand-darkSage text-white shadow-md'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
        {dateRange === 'year' && (
          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700">Select Year:</label>
            <select
              value={customYear}
              onChange={(e) => setCustomYear(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-4 py-2 text-sm focus:border-brand-gold focus:ring-1 focus:ring-brand-gold outline-none"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Showing analytics for: <strong className="text-brand-darkSage">{getDateRangeLabel()}</strong>
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <SafeIcon icon={FiEye} className="text-blue-600" size={20} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Page Views</p>
          </div>
          <p className="text-3xl font-serif text-gray-800">{analytics.pageViews.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">{getDateRangeLabel()}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <SafeIcon icon={FiUsers} className="text-purple-600" size={20} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Unique Visitors</p>
          </div>
          <p className="text-3xl font-serif text-gray-800">{analytics.uniqueVisitors.toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">{getDateRangeLabel()}</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" size={20} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Conversion Rate</p>
          </div>
          <p className="text-3xl font-serif text-gray-800">{analytics.conversionRate.toFixed(2)}%</p>
          <p className="text-xs text-gray-400 mt-1">Visitors → Orders</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <SafeIcon icon={FiShoppingCart} className="text-red-600" size={20} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Cart Abandonment</p>
          </div>
          <p className="text-3xl font-serif text-gray-800">{analytics.cartAbandonmentRate.toFixed(1)}%</p>
          <p className="text-xs text-gray-400 mt-1">Added but not purchased</p>
        </div>
      </div>

      {/* Top Products Section */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Most Viewed Products */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
            <SafeIcon icon={FiEye} className="text-blue-600" size={20} />
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-800">Most Viewed</h3>
          </div>
          <div className="space-y-3">
            {analytics.topProductsViewed.length > 0 ? (
              analytics.topProductsViewed.map((product, idx) => (
                <div key={product.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">#{idx + 1}</span>
                    <ProductLink product={product} />
                  </div>
                  <span className="text-sm font-bold text-blue-600 ml-2 flex-shrink-0">{product.count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
            )}
          </div>
        </div>

        {/* Most Added to Cart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
            <SafeIcon icon={FiShoppingCart} className="text-green-600" size={20} />
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-800">Most Added to Cart</h3>
          </div>
          <div className="space-y-3">
            {analytics.topProductsAdded.length > 0 ? (
              analytics.topProductsAdded.map((product, idx) => (
                <div key={product.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">#{idx + 1}</span>
                    <ProductLink product={product} />
                  </div>
                  <span className="text-sm font-bold text-green-600 ml-2 flex-shrink-0">{product.count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No data yet</p>
            )}
          </div>
        </div>

        {/* Top Selling Products */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
            <SafeIcon icon={FiDollarSign} className="text-amber-600" size={20} />
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-800">Top Selling</h3>
          </div>
          <div className="space-y-3">
            {analytics.topProductsSold.length > 0 ? (
              analytics.topProductsSold.map((product, idx) => (
                <div key={product.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">#{idx + 1}</span>
                    <ProductLink product={product} />
                  </div>
                  <span className="text-sm font-bold text-amber-600 ml-2 flex-shrink-0">{product.count}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-8">No sales yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Top Pages */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          <SafeIcon icon={FiMousePointer} className="text-purple-600" size={20} />
          <h3 className="text-sm font-bold uppercase tracking-widest text-gray-800">Most Visited Pages</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 text-gray-400 text-[10px] uppercase tracking-[0.15em]">
                <th className="p-3 font-bold rounded-tl-lg">Rank</th>
                <th className="p-3 font-bold">Page URL</th>
                <th className="p-3 font-bold text-right rounded-tr-lg">Views</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {analytics.topPages.length > 0 ? (
                analytics.topPages.map((page, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-3">
                      <span className="text-xs font-bold text-gray-400">#{idx + 1}</span>
                    </td>
                    <td className="p-3">
                      <PageLink url={page.url} />
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-sm font-bold text-purple-600">{page.count.toLocaleString()}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-gray-400 text-sm">
                    No page view data yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex gap-3">
          <FiAlertTriangle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="text-sm font-bold text-blue-900 mb-2">About Website Analytics</h4>
            <p className="text-sm text-blue-800 leading-relaxed">
              Analytics data is collected from all visitors (including anonymous users). 
              This helps you understand customer behavior, identify popular products, and optimize your store. 
              Data includes page views, product views, and cart additions to track the complete customer journey.
              Click on any product name or page URL to open it in a new tab.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;